import { Alert, Box, Skeleton } from "@mui/material";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "@/providers";
import { ExpandableCopyText, PageBreadcrumbs } from "@/shared/components";
import { PageLayout } from "@/shared/components/PageLayout";
import { useCollection } from "@/shared/domain/collections/hooks/useCollection";

import { CollectionDetails } from "./CollectionDetails";

/**
 * Details page for a single Collection.
 *
 * The collection to detail is determined from the page's URL.
 * The URL can contain the collection's ID.
 */
export const Collection: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string | undefined }>();
  const { currentOrganization } = useAuth();

  const {
    data: collection,
    error,
    isLoading,
  } = useCollection(currentOrganization?.org_id ? collectionId : undefined);

  const breadcrumbLabel = collection?.name || collectionId || "";

  const breadcrumbs = [
    <Link key="list" to="/collections">
      Collections
    </Link>,
    <ExpandableCopyText
      key="collectionId"
      text={breadcrumbLabel}
      trimLength={36}
    />,
  ];

  if (isLoading && !collection) {
    return (
      <PageLayout
        title={`Roboto - ${breadcrumbLabel}`}
        headerContentLeft={<PageBreadcrumbs>{breadcrumbs}</PageBreadcrumbs>}
      >
        {/* title row */}
        <Skeleton variant="text" width={280} height={32} />
        {/* description */}
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="45%" />
        {/* metadata row */}
        <Skeleton variant="text" width={400} style={{ marginTop: "0.5rem" }} />
        {/* tab bar */}
        <Skeleton
          variant="rectangular"
          width="100%"
          height={36}
          style={{ marginTop: "1rem" }}
        />
        {/* content area */}
        <Skeleton
          variant="rectangular"
          width="100%"
          height={200}
          style={{ marginTop: "0.5rem" }}
        />
      </PageLayout>
    );
  }

  let errorContent = null;
  let pageContent = null;
  if (error) {
    errorContent = (
      <Box mt={2} mb={1}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  } else if (collection) {
    pageContent = <CollectionDetails collection={collection} />;
  }

  return (
    <PageLayout
      title={`Roboto - ${breadcrumbLabel}`}
      headerContentLeft={<PageBreadcrumbs>{breadcrumbs}</PageBreadcrumbs>}
    >
      {errorContent}

      {pageContent}
    </PageLayout>
  );
};
